import { GridColDef } from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  InstructionDialogConfig,
  UseInstructionDialogOutput,
  useInstructionDialog,
} from '~/components/Instructions/useInstructionDialog';
import { AbsenceType } from '~/data/absences/types';
import { useEmployeeAbsenceData } from '~/data/absences/useEmployeeAbsenceData';
import {
  AbsenceDecision,
  AbsenceDecisionDialogProps,
} from '~/features/AbsenceChanges/AbsenceDecisionDialog';
import { EditAbsenceDialogProps } from '~/features/AbsenceChanges/EditAbsenceDialog/component';
import { UseDialogStateOutput, useDialogState } from '~/hooks/useDialogState';
import { AbsencesTableProps } from '~/scenes/PeopleModule/components/Tabs/AbsencesTab/components/AbsencesTable/component';
import { AbsenceDataRow } from '~/scenes/PeopleModule/components/Tabs/AbsencesTab/components/AbsencesTable/data';
import {
  UseAbsencePermissionsOutput,
  useAbsencePermissions,
} from '~/scenes/PeopleModule/components/Tabs/AbsencesTab/components/AbsencesTable/useAbsencePermissions';
import { useAbsenceTableData } from '~/scenes/PeopleModule/components/Tabs/AbsencesTab/components/AbsencesTable/useAbsenceTableData';
import {
  UseAbsenceTableUtilsOutput,
  useAbsenceTableUtils,
} from '~/scenes/PeopleModule/components/Tabs/AbsencesTab/components/AbsencesTable/useAbsenceTableUtils';

type EditDialogState = Pick<EditAbsenceDialogProps, 'open' | 'absenceId'>;
type DecisionDialogState = Pick<
  AbsenceDecisionDialogProps,
  'open' | 'absenceId' | 'decision'
>;

type UseAbsenceTableOutput<V, F> = {
  isLoading: boolean;
  title: string;
  instructionDialog: UseInstructionDialogOutput;
  editDialog: {
    state: EditDialogState;
    onClose: () => void;
    onOpen: (absenceId?: string) => void;
  };
  deleteDialog: {
    state: string | undefined;
    onClose: () => void;
    onOpen: (absenceId: string) => void;
  };
  decisionDialog: {
    state: DecisionDialogState;
    onClose: () => void;
    onOpen: (absenceId: string, decision: AbsenceDecision) => void;
  };
  historyDialogState: UseDialogStateOutput;
  permissions: UseAbsencePermissionsOutput;
  table: {
    rows: AbsenceDataRow[];
    columnConfigs: GridColDef<AbsenceDataRow>[];
    tableUtils: UseAbsenceTableUtilsOutput<AbsenceDataRow, V, F>;
  };
};

export const useAbsenceTable = <V, F>(
  props: AbsencesTableProps,
): UseAbsenceTableOutput<V, F> => {
  const { absenceType, employeeId } = props;

  const { t } = useTranslation();

  // Instruction Dialog
  const instruction = useMemo((): InstructionDialogConfig => {
    return absenceType === AbsenceType.LONG
      ? {
          title: t('laLongTermAbsences'),
          contentKey: 'INSTRUCTIONS_ABSENCE_LONG',
        }
      : {
          title: t('laShortTermAbsences'),
          contentKey: 'INSTRUCTIONS_ABSENCE_SHORT',
        };
  }, [absenceType, t]);

  const instructionDialog = useInstructionDialog(instruction);

  // Edit Dialog
  const [dialogState, setDialogState] = useState<EditDialogState>({
    open: false,
  });

  const onCloseEdit = useCallback(() => {
    setDialogState({
      open: false,
    });
  }, [setDialogState]);

  const onOpenEdit = useCallback(
    (absenceId?: string) => {
      setDialogState({ open: true, absenceId });
    },
    [setDialogState],
  );

  // Delete Dialog
  const [deleteDialogState, setDeleteDialogState] = useState<
    string | undefined
  >(undefined);

  const onCloseDelete = useCallback(() => {
    setDeleteDialogState(undefined);
  }, [setDeleteDialogState]);

  const onOpenDelete = useCallback(
    (absenceId: string) => {
      setDeleteDialogState(absenceId);
    },
    [setDeleteDialogState],
  );

  // Decision Dialog
  const [decisionDialogState, setDecisionDialogState] =
    useState<DecisionDialogState>({
      open: false,
    });

  const onCloseDecision = useCallback(() => {
    setDecisionDialogState({ open: false });
  }, [setDecisionDialogState]);

  const onOpenDecision = useCallback(
    (absenceId: string, decision: AbsenceDecision) => {
      setDecisionDialogState({
        absenceId,
        decision,
        open: true,
      });
    },
    [setDecisionDialogState],
  );

  // History Dialog
  const historyDialogState = useDialogState();

  // Absence Table

  const {
    canAddAbsence,
    canEditAbsences,
    canApproveAbsence,
    canEditSelectedAbsence,
  } = useAbsencePermissions({
    employeeId,
  });

  const tableUtils = useAbsenceTableUtils<AbsenceDataRow, V, F>({
    absenceType,
    canApproveAbsence,
    canEditAbsences,
    employeeId,
  });

  const { absenceEntries, isLoading: isLoadingAbsencesData } =
    useEmployeeAbsenceData({ employeeId });

  const { rows, columnConfigs } = useAbsenceTableData({
    absenceEntries,
    absenceType,
  });

  return {
    isLoading: isLoadingAbsencesData,
    title: instruction.title,
    instructionDialog,
    editDialog: {
      state: dialogState,
      onClose: onCloseEdit,
      onOpen: onOpenEdit,
    },
    deleteDialog: {
      state: deleteDialogState,
      onClose: onCloseDelete,
      onOpen: onOpenDelete,
    },
    decisionDialog: {
      state: decisionDialogState,
      onClose: onCloseDecision,
      onOpen: onOpenDecision,
    },
    historyDialogState,
    permissions: {
      canAddAbsence,
      canEditAbsences,
      canApproveAbsence,
      canEditSelectedAbsence,
    },
    table: {
      rows,
      columnConfigs,
      tableUtils,
    },
  };
};
